// Dourado "Common Expressions" pillar — figurative phrases whose meaning differs
// from their literal words (e.g. `ficar sem` literally "to remain without",
// but really "to run out of"). These trip up learners precisely because the
// word-for-word reading is misleading, so each card shows three layers:
//
//   1. the expression,
//   2. what it *sounds like* word-for-word (the literal trap), and
//   3. what it *actually means*.
//
// This is a STATIC local-data pillar (like tenses / reference) — no PocketBase
// collection, no seed, no pins/notes. It ships entirely in the bundle.
//
// Each row is a terse tuple so the data reads like a table:
//   [ portuguese, literal_gloss, meaning, example_pt, example_en, emoji ]
// The literal_gloss is the deliberately-awkward word-for-word reading; meaning
// is the natural English. Groupings are by the anchoring verb so learners see
// the family resemblance (all the `dar …` phrases together, etc.).

const GROUPS = [
  // ---- ter (to have) ---------------------------------------------------
  {
    category: "ter", rows: [
      ["ter vontade de", "to have will of", "to feel like / to be eager to", "Tenho vontade de viajar este verão.", "I feel like travelling this summer.", "🤩"],
      ["ter pressa", "to have hurry", "to be in a hurry", "Tenho pressa, falamos depois.", "I'm in a hurry, let's talk later.", "⏱️"],
      ["ter razão", "to have reason", "to be right", "Tens razão, enganei-me.", "You're right, I was wrong.", "✅"],
      ["ter cuidado", "to have care", "to be careful", "Tem cuidado com o degrau.", "Be careful with the step.", "⚠️"],
      ["ter lugar", "to have place", "to take place / to happen", "A reunião tem lugar na segunda-feira.", "The meeting takes place on Monday.", "📍"],
      ["ter em conta", "to have in account", "to take into account / to bear in mind", "Tem em conta o trânsito.", "Bear the traffic in mind.", "🧮"],
    ],
  },

  // ---- dar (to give) ---------------------------------------------------
  {
    category: "dar", rows: [
      ["dar igual", "to give equal", "to not matter / to be all the same", "Dá-me igual a cor.", "I don't mind the colour.", "🤷"],
      ["dar-se conta de", "to give oneself account of", "to realise / to notice", "Dei-me conta do erro a tempo.", "I realised the mistake in time.", "💡"],
      ["dar a volta", "to give the turn", "to turn around / to flip over", "Dá a volta à omelete.", "Flip the omelette over.", "🔄"],
      ["dar à luz", "to give to light", "to give birth", "Deu à luz gémeos.", "She gave birth to twins.", "👶"],
      ["dar com", "to give with", "to find / to come across", "Por fim dei com a solução.", "I finally hit on the solution.", "🎯"],
      ["despachar-se", "to dispatch oneself", "to hurry up", "Despacha-te, que estamos atrasados!", "Hurry up, we're going to be late!", "🏃"],
      ["dar os parabéns", "to give the congratulations", "to congratulate (someone)", "Dei os parabéns ao motorista.", "I congratulated the driver.", "🙏"],
    ],
  },

  // ---- fazer (to do / make) --------------------------------------------
  {
    category: "fazer", rows: [
      ["fazer caso", "to make case", "to pay attention / to listen to", "O miúdo não me faz caso.", "The kid doesn't listen to me.", "👂"],
      ["fazer falta", "to make lack", "to be needed / to be necessary", "Faz falta mais tempo.", "More time is needed.", "🧩"],
      ["fazer-se de parvo", "to make oneself the fool", "to play dumb", "Não te faças de parvo comigo.", "Don't play dumb with me.", "🙄"],
      ["fazer fila", "to make line", "to queue / to stand in line", "Fizemos fila durante uma hora.", "We queued for an hour.", "🧍"],
      ["fazer as pazes", "to make the peaces", "to make up / to reconcile", "Discutiram, mas fizeram as pazes.", "They argued, but they made up.", "🤝"],
      ["fazer ponte", "to make bridge", "to take a long weekend", "Na sexta fazemos ponte.", "We're taking Friday off for a long weekend.", "🌉"],
    ],
  },

  // ---- deitar / lançar (to throw, to lay) ------------------------------
  {
    category: "deitar", rows: [
      ["ter saudades de", "to have longings of", "to miss (someone or something)", "Tenho muitas saudades tuas.", "I miss you a lot.", "💔"],
      ["dar uma ajuda", "to give a help", "to lend a hand / to help out", "Dás-me uma ajuda com isto?", "Can you give me a hand with this?", "✋"],
      ["desatar a (chorar/rir)", "to untie to", "to suddenly start (crying/laughing)", "Desatou a chorar de repente.", "She suddenly burst into tears.", "😭"],
      ["deitar a perder", "to lay to lose", "to spoil / to ruin", "A chuva deitou a perder o plano.", "The rain ruined the plan.", "🌧️"],
      ["pôr a culpa", "to put the blame", "to blame (someone)", "Põe sempre a culpa em mim.", "He always blames me.", "🫵"],
    ],
  },

  // ---- ficar (to stay, to remain) --------------------------------------
  {
    category: "ficar", rows: [
      ["ficar sem", "to remain without", "to run out of", "Fiquei sem bateria no telemóvel.", "I ran out of battery on my phone.", "🔋"],
      ["combinar com", "to combine with", "to arrange to meet (someone)", "Combinei com a Ana às seis.", "I arranged to meet Ana at six.", "🤝"],
      ["ficar de", "to remain to", "to agree to (do something)", "Ficámos de nos ver amanhã.", "We agreed to meet tomorrow.", "📆"],
      ["ficar (em casa)", "to remain (at home)", "to stay", "Hoje fico em casa.", "Today I'm staying home.", "🏠"],
      ["ficar bem / mal", "to remain well / badly", "to make a good / bad impression", "Quero ficar bem com os pais dele.", "I want to make a good impression on his parents.", "🌟"],
      ["ficar bem (a alguém)", "to remain well to someone", "to suit / to look good on", "Esse casaco fica-te muito bem.", "That coat really suits you.", "🧥"],
    ],
  },

  // ---- pôr-se (to put oneself) -----------------------------------------
  {
    category: "por-se", rows: [
      ["pôr-se a (fazer algo)", "to put oneself to", "to start / to set about (doing)", "Pôs-se a estudar depois de jantar.", "He started studying after dinner.", "🏁"],
      ["pôr as pilhas", "to put the batteries", "to get one's act together", "Põe as pilhas, resta pouco tempo.", "Get your act together, there's little time left.", "🔋"],
      ["pôr-se de acordo", "to put oneself of agreement", "to reach an agreement", "Não se põem de acordo em nada.", "They can't agree on anything.", "🤝"],
      ["ficar vermelho", "to stay red", "to blush / to go red", "Ficou vermelho de vergonha.", "He went red with embarrassment.", "😳"],
    ],
  },

  // ---- ser / estar -----------------------------------------------------
  {
    category: "ser-estar", rows: [
      ["estar na moda", "to be in the fashion", "to be in fashion / trendy", "Esse estilo está na moda outra vez.", "That style is in fashion again.", "👗"],
      ["estar farto de", "to be fed up of", "to be sick of / fed up with", "Estou farto de esperar.", "I'm fed up of waiting.", "😤"],
      ["ser canja", "to be chicken soup", "to be a piece of cake / very easy", "O exame foi canja.", "The exam was a piece of cake.", "🍞"],
      ["estar nas nuvens", "to be in the clouds", "to have one's head in the clouds", "Estás nas nuvens hoje.", "You're daydreaming today.", "☁️"],
      ["estar a par", "to be at par", "to be up to date / aware", "Estou a par das notícias.", "I'm up to date with the news.", "📰"],
      ["ser uma seca", "to be a dry spell", "to be boring / a drag", "A palestra foi uma seca enorme.", "The talk was a huge drag.", "😴"],
    ],
  },

  // ---- idioms & sayings ------------------------------------------------
  {
    category: "idioms", rows: [
      ["custar os olhos da cara", "to cost the eyes of the face", "to cost an arm and a leg", "O carro custou os olhos da cara.", "The car cost an arm and a leg.", "👁️"],
      ["não ter papas na língua", "to not have porridge on the tongue", "to not mince words / to be blunt", "A minha chefe não tem papas na língua.", "My boss doesn't mince her words.", "👅"],
      ["gozar com alguém", "to enjoy with someone", "to pull someone's leg / to tease", "Estás a gozar comigo?", "Are you pulling my leg?", "😜"],
      ["meter a pata", "to put in the paw", "to put one's foot in it / to mess up", "Meti a pata na reunião.", "I put my foot in it at the meeting.", "🦶"],
      ["estar como uma cabra", "to be like a goat", "to be crazy / nuts", "Aquele tipo está como uma cabra.", "That guy is completely nuts.", "🐐"],
      ["acertar em cheio", "to hit dead-on", "to hit the nail on the head", "Acertaste em cheio com essa ideia.", "You hit the nail on the head with that idea.", "🔨"],
      ["ser unha com carne", "to be nail with flesh", "to be inseparable / thick as thieves", "Aqueles dois são unha com carne.", "Those two are inseparable.", "👫"],
      ["comer à tripa forra", "to eat to the stuffed gut", "to stuff oneself / to eat one's fill", "Comemos à tripa forra no jantar.", "We stuffed ourselves at dinner.", "🥾"],
      ["estar-se nas tintas", "to be in the inks", "to not care at all", "Estou-me nas tintas para o que dizem.", "I couldn't care less what they say.", "🫑"],
      ["ficar de pedra", "to remain of stone", "to be stunned / dumbfounded", "Fiquei de pedra com a notícia.", "I was stunned by the news.", "🗿"],
      ["atirar a toalha ao chão", "to throw the towel on the floor", "to give up / to throw in the towel", "Não atires a toalha ao chão tão cedo.", "Don't give up so soon.", "🏳️"],
      ["procurar pelo em ovo", "to look for hair in an egg", "to overcomplicate / to split hairs", "Não procures pelo em ovo.", "Don't overcomplicate things.", "🐈"],
    ],
  },
];

// Category registry — drives the browse view's filter chips and section headers.
// Order here is the canonical section order on the page.
export const EXPRESSION_CATEGORIES = [
  { id: "ter", label: "With ter", emoji: "🫳" },
  { id: "dar", label: "With dar", emoji: "🎁" },
  { id: "fazer", label: "With fazer", emoji: "🔨" },
  { id: "deitar", label: "With deitar", emoji: "🤲" },
  { id: "ficar", label: "With ficar", emoji: "📌" },
  { id: "por-se", label: "With pôr-se", emoji: "🔄" },
  { id: "ser-estar", label: "With ser & estar", emoji: "🪞" },
  { id: "idioms", label: "Idioms & sayings", emoji: "🎭" },
];

function buildExpressions() {
  const out = [];
  let rank = 1;
  for (const block of GROUPS) {
    for (const [portuguese, literal, meaning, exPt, exEn, emoji] of block.rows) {
      out.push({
        portuguese,
        literal,
        meaning,
        example_pt: exPt,
        example_en: exEn,
        emoji,
        category: block.category,
        rank: rank++,
      });
    }
  }
  return out;
}

// Flattened, ranked list — the single source for the browse view.
export const EXPRESSIONS = buildExpressions();
